import { Fragment, useCallback } from "react";

import { Table, multiSelect } from "../../../../components/Functional";
import Header from "../../../../components/ui/ListHeader";
import { clearSelectionAndOffset } from "../../../../util/clearSelectionState";
import {
  getSearchAwareLabel,
  removeModuleSearchTerm,
  resetModuleSearch,
  useModuleSearchSubmitted,
} from "../../../../util/usePersistedFilterFormik";
import { ClearSearchButton } from "../../../../components/ui/ClearSearchButton";
import { SearchSynopsisHeading } from "../../../../components/ui/SearchSynopsisHeading";
import { useSearchSynopsis } from "../../../../hooks/useSearchSynopsis";

import Actions from "./components/Actions";
import searchedShipments from "./components/SearchedShipments";
import { Link, useNavigate } from "react-router-dom";
import {
  shipmentFilterStore,
  shipmentNavigationStore,
} from "../../components/useShipmentFilters";
import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";
import { buildShipmentNavigationTargetUrl } from "../../searchResultNavigation";

// The Items tab's "Unlocated" checkbox writes this filter, so removing the
// filter's tag has to untick the checkbox too.
const UNLOCATED_FILTER_PATH = "_warehouseMove.locationId";

const removeShipmentSearchTerm = removal => {
  if (removal.path === UNLOCATED_FILTER_PATH) {
    removeModuleSearchTerm("/shipments", { path: "_unlocated" });
  }
  removeModuleSearchTerm("/shipments", removal);
};

const List = props => {
  const { formik } = props || {};
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const hasSearchResults = useModuleSearchSubmitted("shipments");
  const isShowingSearchResults = hasSearchResults && status === "all";
  const { terms: searchSynopsisTerms } = useSearchSynopsis(
    shipmentNavigationStore,
  );

  const actionProps = {
    formik,
    search: shipmentFilterStore.get(),
    onSuccess: () => clearSelectionAndOffset(formik),
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables, searchSynopsisTerms }) => {
      if (status !== "all") return;

      updateSearchResultNavigationStore(shipmentNavigationStore, {
        count,
        edges,
        queryVariables,
        searchSynopsisTerms,
        statusContext: "all",
      });
    },
    [status],
  );

  const handleChange = value => {
    navigate(value === "all" ? "/shipments" : `/shipments?status=${value}`);
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        label: getSearchAwareLabel("All Shipments", hasSearchResults),
        name: "all",
        tableProps: searchedShipments(),
      },
    ],
  };

  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  // function fixes header.name for select lists when status = selectListWorking
  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          status === "selectListWorking"
            ? `item.${header?.name}`
            : header?.name,
      };
    });

  const multiSelectProps = {
    formik,
    label: "Shipments",
    name: "selected",
    keyName: "id",
  };

  const tableProps = {
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "Id",
          name: "id",
          sortKey: "id",
          type: "custom",
          component: ({ values }) => (
            <Link
              className="underline"
              to={buildShipmentNavigationTargetUrl({
                recordId: values?.id,
                status,
              })}
            >
              {values?.id}
            </Link>
          ),
        },
        {
          label: "Delivery Company",
          name: "delCompany",
          sortKey: "delCompany",
          type: "label",
        },
        {
          label: "Delivery Address",
          name: "delAddress",
          sortKey: "delAddress",
          type: "label",
        },
        {
          label: "Origin Name",
          name: "oriName",
          sortKey: "oriName",
          type: "label",
        },
        {
          label: "Origin Company",
          name: "oriCompany",
          sortKey: "oriCompany",
          type: "label",
        },
        {
          label: "Shipment Date",
          name: "date",
          sortKey: "date",
          type: "label",
        },
        {
          label: "Delivered Date",
          name: "delivered",
          sortKey: "delivered",
          type: "label",
        },
        {
          label: "Shipment Currency",
          name: "currency.code",
          sortKey: "currency.code",
          type: "label",
        },
        {
          label: "Items on Shipment",
          name: "itemCount",
          sortKey: "itemCount",
          type: "label",
        },
        {
          label: "Total Value",
          name: "totalValue",
          sortKey: "totalValue",
          type: "currencylabel",
        },
        { label: "", name: "action", type: "custom", component: Actions },
      ],
    }),
    limit: 50,
    onQueryStateChange: captureNavigationSnapshot,
    tabProps,
    variant: "dynamicHeight",
    ...tabTableProps,
  };

  return (
    <Fragment>
      <Header
        leftText={
          isShowingSearchResults ? (
            <SearchSynopsisHeading
              onRemove={removal => {
                clearSelectionAndOffset(formik);
                removeShipmentSearchTerm(removal);
              }}
              terms={searchSynopsisTerms}
            />
          ) : null
        }
        leftAction={
          isShowingSearchResults ? (
            <ClearSearchButton
              onClick={() => resetModuleSearch("/shipments")}
            />
          ) : null
        }
        rightComponent={() => {
          return <div className="flex gap-4">{tabActions?.(actionProps)}</div>;
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </Fragment>
  );
};

export default List;
